import { Location, FilterState, PagedResult, LocationFormValues, LocationAuditEntry } from './locationTypes';
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';
import type { MoreAttributesFilterState } from './components/MoreAttributesDrawer';

const API_BASE = '/admin/locations';
const FILTER_OPTIONS_BASE = `${API_BASE}/filter-options`;

export type LocationSortDirection = 'ASC' | 'DESC' | 'DEFAULT';

export type LocationSearchParams = {
  q: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: LocationSortDirection;
  filters?: FilterState;
  moreAttributes?: MoreAttributesFilterState;
};

export interface LocationSortParams {
  sortBy?: string;
  sortDirection?: LocationSortDirection;
}

export interface LocationFilterOptionsResponse {
  stateProvince: string[];
  ownership: string[];
  warehouseSystem: string[];
  laborModel: string[];
  tplSupplier: string[];
  regionLead: string[];
  crossdockBuildSite1: string[];
  crossdockBuildSite2: string[];
  centralizedBulkSite: string[];
}

export type LocationFilterField = keyof LocationFilterOptionsResponse;
export type LocationUniquenessField = 'loc-id' | 'sap-id';

export interface LocationUniquenessResult {
  isUnique: boolean;
}

// Authenticated axios instance — interceptor auto-injects Bearer token on every request
const api = createApiClient();

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const response = (error as any).response;
    const data = response?.data ?? {};
    const detail = data.error ?? data.message ?? data.detail
      ?? (Array.isArray(data.errors) ? data.errors.map((entry: any) => entry.message ?? entry).join(', ') : null);
    if (detail) return String(detail);
    const status = response?.status;
    if (status != null) return `${fallback} (${status})`;
  }

  return fallback;
}

async function checkLocationUniqueness(field: LocationUniquenessField, value: string): Promise<LocationUniquenessResult> {
  const params = new URLSearchParams({ value: value.trim() });
  const endpoint = field === 'loc-id' ? 'loc-id' : 'sap-id';
  const { data } = await api.get(`${API_BASE}/check/${endpoint}?${params}`);
  return { isUnique: Boolean(data?.isUnique) };
}

const SIZE_DISPLAY: Record<string, string> = {
  LARGE: 'Large', MED: 'Medium', SML: 'Small',
  Large: 'Large', Medium: 'Medium', Small: 'Small',
};

function formatWarehouseStartTime(rawValue: unknown): string {
  if (rawValue == null || rawValue === '') return '';

  const value = String(rawValue).trim();
  const amPmMatch = value.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?\s*([AP]M)$/i);
  if (amPmMatch) {
    const hour = Number(amPmMatch[1]);
    const minutes = amPmMatch[2];
    const period = amPmMatch[3].toUpperCase();
    return `${hour}:${minutes} ${period}`;
  }

  const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (!timeMatch) return value;

  const hour24 = Number(timeMatch[1]);
  const minutes = timeMatch[2];
  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) return value;

  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
}

function mapApiToLocation(raw: any): Location {
  return {
    id:              raw.locId                ?? '',
    sap:             raw.locSapId             ?? '',
    name:            raw.locName              ?? '',
    facilityType:    raw.locationFacilityType ?? '',
    type:            raw.locationType         ?? '',
    org:             raw.organizationType     ?? '',
    tpl:             raw.is3pl === true ? 'Yes' : raw.is3pl === false ? 'No' : '',
    size:            SIZE_DISPLAY[raw.size]   ?? raw.size ?? '',
    region:          raw.region               ?? '',
    market:          raw.market               ?? '',
    marketLead:      raw.marketLead           ?? '',
    regionLead:      raw.regionLead           ?? '',
    coe:             raw.coeResource          ?? '',
    coe2:            raw.secondaryCoeResource ?? '',
    status:          raw.status               ?? '',
    empCount:        raw.headcount            ?? 0,
    isDuo:           raw.isDuo                ?? false,
    executionSystem: raw.executionSystem      ?? '',
    edfSynced:       raw.edfSynced            ?? false,
    address:         raw.streetAddress        ?? '',
    city:            raw.city                 ?? '',
    state:           raw.stateProvince        ?? '',
    zip:             raw.postalCode           ?? '',
    latitude:        raw.latitude  != null ? String(raw.latitude)  : '',
    longitude:       raw.longitude != null ? String(raw.longitude) : '',
    ownership:       raw.ownership            ?? '',
    yearBuilt:       raw.yearBuilt != null ? String(raw.yearBuilt) : '',
    warehouseSystem: raw.warehouseSystem      ?? '',
    warehouseStartTime: formatWarehouseStartTime(raw.warehouseStartTime),
    labor:           raw.laborModel           ?? '',
    union:           raw.unionStatus          ?? '',
    supplier:        raw.tplSupplier          ?? '',
    totalSqft:       raw.totalBuildingSqft   != null ? String(raw.totalBuildingSqft)   : '',
    operationalSqft: raw.operationalSqft     != null ? String(raw.operationalSqft)     : '',
    pickedVolume:    raw.pickedVolume        != null ? String(raw.pickedVolume)        : '',
    salesVolume:     raw.salesVolume         != null ? String(raw.salesVolume)         : '',
    productionVolume: raw.productionVolume    != null ? String(raw.productionVolume)    : '',
    docks:           raw.dockCount           != null ? String(raw.dockCount)           : '',
    docksEng:        raw.docksWithENGControls    != null ? String(raw.docksWithENGControls)    : '',
    docksNoEng:      raw.docksWithoutENGControls != null ? String(raw.docksWithoutENGControls) : '',
    driveIn:         raw.driveInBays         != null ? String(raw.driveInBays)         : '',
    bottleLines:     raw.bottleLines         != null ? String(raw.bottleLines)         : '',
    canLines:        raw.canLines            != null ? String(raw.canLines)            : '',
    build1:          raw.crossdockBuildSite1   ?? '',
    build2:          raw.crossdockBuildSite2   ?? '',
    bulkSite:        raw.centralizedBulkSite   ?? '',
  };
}

function normalizeSortParams(sortBy?: string, sortDirection?: LocationSortDirection): LocationSortParams {
  let normalizedSortBy = sortBy?.trim() || undefined;
  let normalizedSortDirection = sortDirection;

  if (normalizedSortBy) {
    const legacyPrefixMatch = normalizedSortBy.match(/^([+-])(.+)$/);
    const legacyDirectionMatch = normalizedSortBy.match(/^(asc|desc):(.+)$/i);

    if (legacyPrefixMatch) {
      normalizedSortDirection = normalizedSortDirection ?? (legacyPrefixMatch[1] === '-' ? 'DESC' : 'ASC');
      normalizedSortBy = legacyPrefixMatch[2];
    } else if (legacyDirectionMatch) {
      normalizedSortDirection = normalizedSortDirection ?? (legacyDirectionMatch[1].toUpperCase() as LocationSortDirection);
      normalizedSortBy = legacyDirectionMatch[2];
    }
  }

  if (!normalizedSortDirection && normalizedSortBy) {
    normalizedSortDirection = 'DEFAULT';
  }

  return {
    sortBy: normalizedSortBy,
    sortDirection: normalizedSortDirection,
  };
}

function buildLocationRequestBody(
  f: FilterState,
  page: number,
  pageSize: number,
  sortBy?: string,
  sortDirection?: LocationSortDirection,
  moreAttributes?: MoreAttributesFilterState,
  q?: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    page,
    pageSize,
  };

  if (q != null) {
    const trimmedQuery = q.trim();
    if (!trimmedQuery) {
      throw new Error('Search text is required.');
    }
    body.q = trimmedQuery;
  }

  const appendMulti = (key: string, values?: string[] | string | null) => {
    const normalizedValues = Array.isArray(values)
      ? values
      : values != null && values !== ''
        ? [values]
        : [];

    const cleanedValues = normalizedValues
      .map(value => String(value).trim())
      .filter(value => value !== '');

    if (cleanedValues.length > 0) {
      body[key] = cleanedValues;
    }
  };

  appendMulti('locName', f.locationNames);
  appendMulti('locationType', f.type);
  appendMulti('locationFacilityType', f.facilityType);
  appendMulti('organizationType', f.org);
  appendMulti('unionStatus', f.unionStatus);
  appendMulti('size', f.size);
  appendMulti('region', f.region);
  appendMulti('market', f.market);
  appendMulti('status', f.status);

  // is3pl is a single Boolean on the backend — only apply when one side is selected
  if (f.tpl.length === 1) {
    body.is3pl = f.tpl[0] === 'Yes';
  }

  if (moreAttributes) {
    appendMulti('ownership', moreAttributes.ownership);
    appendMulti('coeResource', moreAttributes.coeResource);
    appendMulti('secondaryCoeResource', moreAttributes.secondaryCoeResource);
    appendMulti('regionLead', moreAttributes.regionLead);
    appendMulti('stateProvince', moreAttributes.stateProvince);
    appendMulti('warehouseSystem', moreAttributes.warehouseSystem);
    appendMulti('laborModel', moreAttributes.labor);
    appendMulti('unionStatus', moreAttributes.union);
    appendMulti('tplSupplier', moreAttributes.supplier);

    if (moreAttributes.isDuo.length === 1) {
      body.isDuo = moreAttributes.isDuo[0] === 'true';
    }

    const appendRange = (minKey: string, maxKey: string, minValue: string, maxValue: string) => {
      const trimmedMin = minValue.trim();
      const trimmedMax = maxValue.trim();
      if (trimmedMin !== '') body[minKey] = Number(trimmedMin);
      if (trimmedMax !== '') body[maxKey] = Number(trimmedMax);
    };

    appendRange('yearBuiltMin', 'yearBuiltMax', moreAttributes.yearBuiltMin, moreAttributes.yearBuiltMax);
    appendRange('pickedVolumeMin', 'pickedVolumeMax', moreAttributes.pickedVolumeMin, moreAttributes.pickedVolumeMax);
    appendRange('totalBuildingSqftMin', 'totalBuildingSqftMax', moreAttributes.totalSqftMin, moreAttributes.totalSqftMax);
    appendRange('operationalSqftMin', 'operationalSqftMax', moreAttributes.operationalSqftMin, moreAttributes.operationalSqftMax);
    appendRange('dockCountMin', 'dockCountMax', moreAttributes.docksMin, moreAttributes.docksMax);
    appendRange('docksWithEngControlsMin', 'docksWithEngControlsMax', moreAttributes.docksEngMin, moreAttributes.docksEngMax);
    appendRange('docksWithoutEngControlsMin', 'docksWithoutEngControlsMax', moreAttributes.docksNoEngMin, moreAttributes.docksNoEngMax);
    appendRange('driveInBaysMin', 'driveInBaysMax', moreAttributes.driveInMin, moreAttributes.driveInMax);
    appendRange('bottleLinesMin', 'bottleLinesMax', moreAttributes.bottleLinesMin, moreAttributes.bottleLinesMax);
    appendRange('canLinesMin', 'canLinesMax', moreAttributes.canLinesMin, moreAttributes.canLinesMax);

    appendMulti('crossdockBuildSite1', moreAttributes.build1);
    appendMulti('crossdockBuildSite2', moreAttributes.build2);
    appendMulti('centralizedBulkSite', moreAttributes.bulkSite);
  }

  const normalizedSort = normalizeSortParams(sortBy, sortDirection);
  if (normalizedSort.sortBy) body.sortBy = normalizedSort.sortBy;
  if (normalizedSort.sortDirection) body.sortDirection = normalizedSort.sortDirection;

  return body;
}

function parsePagedResponse(data: any, page: number, size: number): PagedResult {
  const rows: any[] = Array.isArray(data)
    ? data
    : (data.content ?? data.data ?? []);
  const totalElements: number = data.totalElements ?? data.totalCount ?? rows.length;
  const totalPages: number    = data.totalPages    ?? Math.ceil(totalElements / size);
  return { items: rows.map(mapApiToLocation), totalElements, totalPages, page, size };
}

export const locationApi = {
  getAll: async (
    f: FilterState,
    page = 0,
    size = 100,
    sortBy?: string,
    sortDirection?: LocationSortDirection,
    moreAttributes?: MoreAttributesFilterState,
  ): Promise<PagedResult> => {
    const body = buildLocationRequestBody(f, page, size, sortBy, sortDirection, moreAttributes);
    const { data } = await api.post(`${API_BASE}/all`, body);
    return parsePagedResponse(data, page, size);
  },

  search: async (
    q: string,
    f: FilterState,
    page = 0,
    pageSize = 100,
    sortBy?: string,
    sortDirection?: LocationSortDirection,
    moreAttributes?: MoreAttributesFilterState,
  ): Promise<PagedResult> => {
    const body = buildLocationRequestBody(f, page, pageSize, sortBy, sortDirection, moreAttributes, q);
    const { data } = await api.post(`${API_BASE}/search`, body);
    return parsePagedResponse(data, page, pageSize);
  },

  getById: async (id: string): Promise<Location> => {
    const { data } = await api.get(`${API_BASE}/${encodeURIComponent(id)}`);
    return mapApiToLocation(data.data ?? data);
  },

  /** Create location using unified LocationFormValues (#306064) */
  create: async (values: LocationFormValues): Promise<Location> => {
    const n = (v: string) => (v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : null);
    const payload = {
      locId: values.id, locSapId: values.sap, locName: values.name, status: 'Active',
      locationFacilityType: values.facilityType || null,
      locationType: values.type, organizationType: values.org,
      is3pl: values.tpl === 'Yes', size: values.size,
      region: values.region, market: values.market,
      marketLead: values.marketLead || null, regionLead: values.regionLead || null,
      coeResource: values.coe || null, secondaryCoeResource: values.coe2 || null,
      executionSystem: values.executionSystem || null,
      streetAddress: values.address || null, city: values.city || null,
      stateProvince: values.state || null, postalCode: values.zip || null,
      latitude: n(values.latitude), longitude: n(values.longitude),
      ownership: values.ownership || null, yearBuilt: n(values.yearBuilt),
      warehouseSystem: values.warehouseSystem || null,
      laborModel: values.labor || null, unionStatus: values.union || null,
      tplSupplier: values.supplier || null,
      totalBuildingSqft: n(values.totalSqft), operationalSqft: n(values.operationalSqft),
      pickedVolume: n(values.pickedVolume),
      dockCount: n(values.docks), docksWithENGControls: n(values.docksEng), docksWithoutENGControls: n(values.docksNoEng),
      driveInBays: n(values.driveIn), bottleLines: n(values.bottleLines), canLines: n(values.canLines),
      crossdockBuildSite1: values.build1 || null, crossdockBuildSite2: values.build2 || null, centralizedBulkSite: values.bulkSite || null,
    };
    const qs = new URLSearchParams({
      clientTimezone: getTimezoneOffsetString(), clientTimestamp: getLocalTimestamp(),
    }).toString();
    try {
      const { data } = await api.post(`${API_BASE}?${qs}`, payload);
      return mapApiToLocation(data.data ?? data);
    } catch (e: any) {
      throw new Error(getApiErrorMessage(e, `Failed to create location (${e.response?.status ?? 'unknown'})`));
    }
  },

  /** Full update using LocationFormValues (new unified form, #306064) */
  updateFull: async (id: string, values: LocationFormValues): Promise<Location> => {
    const n = (v: string) => (v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : null);
    const payload = {
      locId: values.id, locSapId: values.sap, locName: values.name, status: values.status,
      locationFacilityType: values.facilityType || null,
      locationType: values.type, organizationType: values.org,
      is3pl: values.tpl === 'Yes', size: values.size,
      region: values.region, market: values.market,
      marketLead: values.marketLead || null, regionLead: values.regionLead || null,
      coeResource: values.coe || null, secondaryCoeResource: values.coe2 || null,
      executionSystem: values.executionSystem || null,
      streetAddress: values.address || null, city: values.city || null,
      stateProvince: values.state || null, postalCode: values.zip || null,
      latitude: n(values.latitude), longitude: n(values.longitude),
      ownership: values.ownership || null, yearBuilt: n(values.yearBuilt),
      warehouseSystem: values.warehouseSystem || null,
      laborModel: values.labor || null, unionStatus: values.union || null,
      tplSupplier: values.supplier || null,
      totalBuildingSqft: n(values.totalSqft), operationalSqft: n(values.operationalSqft),
      pickedVolume: n(values.pickedVolume),
      dockCount: n(values.docks), docksWithENGControls: n(values.docksEng), docksWithoutENGControls: n(values.docksNoEng),
      driveInBays: n(values.driveIn), bottleLines: n(values.bottleLines), canLines: n(values.canLines),
      crossdockBuildSite1: values.build1 || null, crossdockBuildSite2: values.build2 || null, centralizedBulkSite: values.bulkSite || null,
    };
    const qs = new URLSearchParams({
      clientTimezone: getTimezoneOffsetString(), clientTimestamp: getLocalTimestamp(),
    }).toString();
    try {
      const { data } = await api.put(`${API_BASE}/${encodeURIComponent(id)}?${qs}`, payload);
      return mapApiToLocation(data.data ?? data);
    } catch (e: any) {
      throw new Error(getApiErrorMessage(e, `Failed to update location (${e.response?.status ?? 'unknown'})`));
    }
  },

  /**
   * Bulk update — applies only changed values to all IDs.
   * Returns successLocIds, failures (with reasons).
   * (#306064, #168467)
   */
  bulkUpdate: async (
    ids: string[],
    changes: Partial<LocationFormValues>,
  ): Promise<{ updatedIds: string[]; failedIds: { id: string; reason: string }[]; audit: LocationAuditEntry[] }> => {
    const n = (v: string) => (v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : null);
    const NUM_FIELDS = ['totalSqft','operationalSqft','pickedVolume','docks','docksEng','docksNoEng',
      'driveIn','bottleLines','canLines','yearBuilt','latitude','longitude'];
    const MAP: Record<string, string> = {
      facilityType: 'locationFacilityType', type: 'locationType', org: 'organizationType',
      tpl: 'is3pl', size: 'size', ownership: 'ownership', yearBuilt: 'yearBuilt',
      region: 'region', market: 'market', marketLead: 'marketLead', regionLead: 'regionLead',
      coe: 'coeResource', coe2: 'secondaryCoeResource', executionSystem: 'executionSystem',
      warehouseSystem: 'warehouseSystem', labor: 'laborModel', union: 'unionStatus',
      supplier: 'tplSupplier', totalSqft: 'totalBuildingSqft', operationalSqft: 'operationalSqft',
      pickedVolume: 'pickedVolume',
      docks: 'dockCount', docksEng: 'docksWithENGControls', docksNoEng: 'docksWithoutENGControls', driveIn: 'driveInBays',
      bottleLines: 'bottleLines', canLines: 'canLines',
      build1: 'crossdockBuildSite1', build2: 'crossdockBuildSite2', bulkSite: 'centralizedBulkSite',
      address: 'streetAddress', state: 'stateProvince', zip: 'postalCode',
    };
    const payload: Record<string, any> = { locIds: ids };
    Object.entries(changes).forEach(([key, value]) => {
      if (value === '' || value == null) return;
      const apiKey = MAP[key] ?? key;
      if (apiKey === 'is3pl') { payload[apiKey] = value === 'Yes'; return; }
      payload[apiKey] = NUM_FIELDS.includes(key) ? n(String(value)) : value;
    });
    try {
      const { data } = await api.patch(`${API_BASE}/bulk-update`, payload);
      return {
        updatedIds: data.successLocIds ?? data.updatedIds ?? ids,
        failedIds:  (data.failures ?? []).map((f: any) => ({ id: f.locId, reason: f.errorMessage })),
        audit:      [],
      };
    } catch (e: any) {
      const err = e.response?.data ?? {};
      throw new Error(err.error ?? err.message ?? err.detail
        ?? `Bulk update failed (${e.response?.status ?? 'unknown'})`);
    }
  },

  updateStatus: async (id: string, status: string, reason?: string): Promise<void> => {
    try {
      await api.patch(`${API_BASE}/${encodeURIComponent(id)}/status`, {
        status,
        ...(reason ? { reason } : {}),
      });
    } catch (e: any) {
      const err = e.response?.data ?? {};
      const detail = err.error ?? err.message ?? err.detail
        ?? `Failed to update status (${e.response?.status ?? 'unknown'})`;
      throw new Error(detail);
    }
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_BASE}/${encodeURIComponent(id)}`);
  },

  checkLocId: async (locId: string, excludeId?: number): Promise<boolean> => {
    const params = new URLSearchParams({ value: locId });
    if (excludeId != null) params.set('excludeId', String(excludeId));
    try {
      const { data } = await api.get(`${API_BASE}/check/loc-id?${params}`);
      // Backend returns { isUnique: true }
      return data.isUnique ?? (data.available ?? data.unique ?? !data.exists);
    } catch {
      return true;
    }
  },

  checkSapId: async (sapId: string, excludeId?: number): Promise<boolean> => {
    const params = new URLSearchParams({ value: sapId });
    if (excludeId != null) params.set('excludeId', String(excludeId));
    try {
      const { data } = await api.get(`${API_BASE}/check/sap-id?${params}`);
      return data.isUnique ?? (data.available ?? data.unique ?? !data.exists);
    } catch {
      return true;
    }
  },

  checkLocationUniqueness: async (field: LocationUniquenessField, value: string): Promise<LocationUniquenessResult> => {
    try {
      return await checkLocationUniqueness(field, value);
    } catch (error) {
      throw new Error(getApiErrorMessage(error, `Failed to verify ${field === 'loc-id' ? 'LOC ID' : 'SAP ID'} uniqueness.`));
    }
  },

  getRegions: async (): Promise<string[]> => {
    const { data } = await api.get(`${API_BASE}/regions`);
    return Array.isArray(data) ? data : (data.data ?? []);
  },

  getMarkets: async (): Promise<string[]> => {
    const { data } = await api.get(`${API_BASE}/markets`);
    return Array.isArray(data) ? data : (data.data ?? []);
  },

  getMarketsByRegion: async (region: string | string[]): Promise<string[]> => {
    const params = new URLSearchParams();
    const regions = Array.isArray(region) ? region : [region];
    regions
      .map(value => value.trim())
      .filter(Boolean)
      .forEach(value => params.append('region', value));
    const { data } = await api.get(`${API_BASE}/markets-by-region?${params}`);
    return Array.isArray(data) ? data : (data.data ?? []);
  },

  getFilterOptions: async (): Promise<LocationFilterOptionsResponse> => {
    const { data } = await api.get(FILTER_OPTIONS_BASE);
    return data?.data ?? data ?? {
      stateProvince: [],
      ownership: [],
      warehouseSystem: [],
      laborModel: [],
      tplSupplier: [],
      regionLead: [],
      crossdockBuildSite1: [],
      crossdockBuildSite2: [],
      centralizedBulkSite: [],
    };
  },

  searchFilterOptions: async (field: LocationFilterField, search?: string): Promise<{ field: LocationFilterField; values: string[] }> => {
    const params = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    const { data } = await api.get(`${FILTER_OPTIONS_BASE}/${encodeURIComponent(field)}${params}`);
    return data?.data ?? data ?? { field, values: [] };
  },
};
